/**
 * The sign-in screen.
 *
 * Deliberately plain — it is a gate, not a destination. The one thing it does
 * carefully is error handling: the server refuses to say *which* half of the
 * credentials was wrong, and this screen must not undo that by guessing.
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { api } from "../api.ts";
import { Button } from "./ui/button.tsx";
import { Input } from "./ui/input.tsx";
import { Label } from "./ui/label.tsx";
import { Alert, AlertDescription } from "./ui/alert.tsx";

export function LoginScreen({ onSignedIn }: { onSignedIn: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);

    try {
      await api.login({ username, password });
      onSignedIn();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Sign-in failed.");
      setPassword("");
      setBusy(false);
    }
  };

  return (
    <main className="grid min-h-dvh place-items-center bg-background px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm"
        aria-labelledby="signin-heading"
      >
        <h1 id="signin-heading" className="mb-1 text-[1.9rem] tracking-[0.01em]">
          sZero
        </h1>
        <p className="mb-8 font-sans text-sm text-muted-foreground">
          Sign in to continue.
        </p>

        {error && (
          <Alert variant="destructive" className="mb-4 font-sans">
            {/* Announced, because a sighted user sees it appear and a screen
                reader otherwise gets nothing after pressing the button. */}
            <AlertDescription role="alert">{error}</AlertDescription>
          </Alert>
        )}

        <div className="mb-4 grid gap-2">
          <Label htmlFor="username" className="font-sans">
            Username
          </Label>
          <Input
            id="username"
            ref={usernameRef}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />
        </div>

        <div className="mb-6 grid gap-2">
          <Label htmlFor="password" className="font-sans">
            Password
          </Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <Button type="submit" disabled={busy} className="w-full font-sans">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </main>
  );
}
